/**
 * Cascade Configuration Contracts
 *
 * DATABASE-FIRST: Schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-02
 *
 * Tables: cascade_new, cascade_config_new, payment_services
 * Functions: enable_cascade_new, disable_cascade_new, get_active_cascade_new, set_method_position_new
 *
 * Used by:
 * - backend → payment-service (config endpoints)
 * - admin panel → backend (cascade management)
 */
import { z } from 'zod';
import { timestampString } from '../common.js';
// =============================================================================
// PAYMENT SERVICE TYPE
// Source: CHECK constraint 'valid_type' on payment_services table
// =============================================================================
export const paymentServiceTypeValues = ['deposit', 'withdrawal'];
export const paymentServiceTypeSchema = z.enum(paymentServiceTypeValues);
// =============================================================================
// CASCADE EVENT TYPE
// For logging cascade attempts
// =============================================================================
export const cascadeEventValues = ['skipped', 'failed', 'success'];
export const cascadeEventSchema = z.enum(cascadeEventValues);
// =============================================================================
// PAYMENT SERVICE ENTITY
// Matches: payment_services table columns exactly
// =============================================================================
export const paymentServiceEntitySchema = z.object({
    id: z.number(),
    hash: z.string(), // Stable identifier (e.g., 'fservice')
    name: z.string(), // Display name (e.g., 'FastPay')
    type: paymentServiceTypeSchema,
    createdAt: timestampString,
    turnedOff: timestampString, // When disabled (null = active)
    restarted: timestampString, // When re-enabled
});
// =============================================================================
// CASCADE ENTITY
// Matches: cascade_new table columns exactly
// =============================================================================
export const cascadeEntitySchema = z.object({
    id: z.number(),
    deposits: z.array(z.record(z.unknown())), // JSONB array of deposit methods
    withdrawals: z.array(z.record(z.unknown())), // JSONB array of withdrawal methods
    startedAt: timestampString,
    endedAt: timestampString,
});
// =============================================================================
// CASCADE CONFIG ENTITY
// Matches: cascade_config_new table columns exactly
// =============================================================================
export const cascadeConfigEntitySchema = z.object({
    id: z.number(),
    cascadeId: z.number(),
    depositPositions: z.record(z.unknown()), // JSONB: { hash: position }
    withdrawalPositions: z.record(z.unknown()), // JSONB: { hash: position }
    previousDepositPositions: z.record(z.unknown()).nullable(),
    previousWithdrawalPositions: z.record(z.unknown()).nullable(),
    staffId: z.string().nullable(),
    createdAt: timestampString,
});
// =============================================================================
// CASCADE PROVIDER (API response shape)
// Used in config endpoints
// =============================================================================
/**
 * Provider info in cascade config response
 */
export const cascadeProviderSchema = z.object({
    position: z.number(),
    method: z.string(), // method name (e.g., 'FastPay')
    hash: z.string(), // stable identifier (e.g., 'fservice')
    displayName: z.string(),
    minAmount: z.number(),
    maxAmount: z.number().nullable(), // DB allows null (no max)
});
// =============================================================================
// DEPOSIT CASCADE CONFIG RESPONSE
// Endpoint: GET /api/internal/cascade/deposit/config
// =============================================================================
export const depositCascadeConfigResponseSchema = z.object({
    success: z.boolean(),
    enabled: z.boolean(),
    providers: z.array(cascadeProviderSchema),
    error: z.string().optional(),
});
// =============================================================================
// WITHDRAWAL CASCADE CONFIG RESPONSE
// Endpoint: GET /api/internal/cascade/withdrawal/config
// =============================================================================
export const withdrawalCascadeConfigResponseSchema = z.object({
    success: z.boolean(),
    providers: z.array(cascadeProviderSchema),
    error: z.string().optional(),
});
// =============================================================================
// CASCADE LOG EVENT
// Endpoint: POST /api/internal/cascade/log-event
// =============================================================================
/**
 * Cascade log event request (statistics)
 */
export const cascadeLogEventRequestSchema = z.object({
    date: z.string(), // YYYY-MM-DD
    method: z.string(), // provider hash
    event: cascadeEventSchema,
    error: z.string().optional(),
    durationMs: z.number().optional(),
});
/**
 * Cascade log event response
 */
export const cascadeLogEventResponseSchema = z.object({
    success: z.boolean(),
});
// =============================================================================
// ENABLE CASCADE
// Function: enable_cascade_new(p_deposit_hashes jsonb, p_withdrawal_hashes jsonb)
// Returns: integer (cascade_id)
// =============================================================================
/**
 * Enable cascade input
 */
export const enableCascadeInputSchema = z.object({
    depositHashes: z.array(z.string()), // Array of provider hashes
    withdrawalHashes: z.array(z.string()),
});
/**
 * Enable cascade result
 */
export const enableCascadeResultSchema = z.object({
    cascadeId: z.number(),
});
// =============================================================================
// GET ACTIVE CASCADE
// Function: get_active_cascade_new()
// Returns: TABLE(id integer, deposits jsonb, withdrawals jsonb, started_at timestamptz)
// =============================================================================
export const getActiveCascadeResultSchema = z.object({
    id: z.number(),
    deposits: z.array(z.record(z.unknown())),
    withdrawals: z.array(z.record(z.unknown())),
    startedAt: z.string().datetime(),
}).nullable();
// =============================================================================
// SET METHOD POSITION
// Function: set_method_position_new(p_hash varchar, p_position integer, p_type varchar, p_staff_id varchar)
// Returns: jsonb
// =============================================================================
export const setMethodPositionInputSchema = z.object({
    hash: z.string(),
    position: z.number(),
    type: paymentServiceTypeSchema,
    staffId: z.string(),
});
export const setMethodPositionResultSchema = z.record(z.unknown());
// =============================================================================
// CASCADE V2 INFO (Admin endpoint)
// Endpoint: GET /api/internal/cascade/v2/info
// =============================================================================
export const cascadeInfoResponseSchema = z.object({
    success: z.boolean(),
    enabled: z.boolean(),
    cascadeId: z.number().nullable(),
    startedAt: z.string().datetime().nullable(),
    depositMethods: z.array(cascadeProviderSchema),
    withdrawalMethods: z.array(cascadeProviderSchema),
});
//# sourceMappingURL=cascade.js.map